/**
 * Super Signal tab — walk-forward ML combination study.
 * 4 approaches compared strictly out-of-sample, with mandatory interpretability.
 */
import React, { useMemo, useState } from "react";
import {
  Bar, BarChart, CartesianGrid, Cell, ReferenceLine,
  ResponsiveContainer, Tooltip, XAxis, YAxis,
} from "recharts";
import { Badge } from "../ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { AlphaVal, RunBar, SigBadge, StatCard, ToggleGroup } from "./common";
import { CAP_LABELS } from "./CapBucketBlock";

const APPROACH_ORDER = ["ew_rank", "ic_weighted", "ridge", "gbt"];

const FEATURE_LABELS = {
  sector_centrality: "Sector Centrality",
  global_centrality: "Global Centrality",
  dynamic_upside: "Dynamic Upside",
  target_age_days: "Target Age",
  grad_1m: "Nowcast Grad 1M",
  grad_3m: "Nowcast Grad 3M",
  confidence: "Nowcast Confidence",
  log_n_funds: "Fund Breadth (log)",
  mom_63_sec: "3M Sector Momentum (control)",
  rev_21: "1M Reversal (control)",
  atm_iv: "ATM Implied Vol",
  iv_skew: "IV Skew",
  iv_spread: "IV Spread (C−P)",
  risk_reversal: "Risk Reversal",
  cp_volume_ratio: "Call Volume Share",
  term_slope: "IV Term Slope",
  os_ratio: "Option/Stock Volume",
  vrp: "Variance Risk Premium",
  cap_code: "Cap Segment",
};
const featLabel = (f) =>
  FEATURE_LABELS[f] || (f.startsWith("cluster_") ? `Regime ${f.slice(8)}` : f);

const ImportanceChart = ({ data, title, testId }) => {
  const rows = useMemo(
    () =>
      Object.entries(data || {})
        .map(([f, v]) => ({ name: featLabel(f), value: v }))
        .sort((a, b) => Math.abs(b.value) - Math.abs(a.value))
        .slice(0, 12),
    [data]
  );
  if (!rows.length) return null;
  return (
    <div data-testid={testId}>
      <div className="text-sm font-medium mb-2">{title}</div>
      <ResponsiveContainer width="100%" height={Math.max(160, rows.length * 26)}>
        <BarChart data={rows} layout="vertical" margin={{ left: 60, right: 20 }}>
          <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
          <XAxis type="number" tick={{ fontSize: 10 }} />
          <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={150} />
          <Tooltip
            content={({ active, payload }) =>
              active && payload?.length ? (
                <div className="bg-popover border rounded-md px-2 py-1 text-xs font-mono">
                  {payload[0].payload.name}: {Number(payload[0].value).toFixed(4)}
                </div>
              ) : null
            }
          />
          <ReferenceLine x={0} stroke="currentColor" opacity={0.3} />
          <Bar dataKey="value" radius={[0, 3, 3, 0]}>
            {rows.map((d, i) => (
              <Cell key={i} fill={d.value >= 0 ? "#6366f1" : "#f59e0b"} fillOpacity={0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export const SuperSignalTab = ({ signal }) => {
  const { result, job, running, run } = signal;
  const [selected, setSelected] = useState(null);
  const [bench, setBench] = useState("sector");
  const [universe, setUniverse] = useState("investable");
  const res = result?.result;

  const approaches = universe === "investable"
    ? res?.approaches
    : res?.approaches_all_caps || res?.approaches;
  const bestKey = universe === "investable"
    ? res?.best_approach
    : res?.best_approach_all_caps || res?.best_approach;

  const active = selected || bestKey || "gbt";
  const approach = approaches?.[active];
  const ev = approach?.[bench];

  const decileData = useMemo(
    () =>
      (ev?.quantile_means_pct || []).map((v, i) => ({
        name: `D${i + 1}`,
        alpha: v,
      })),
    [ev]
  );

  return (
    <div className="space-y-4" data-testid="supersignal-tab">
      <Card>
        <CardHeader>
          <CardTitle>Super Signal — Walk-Forward Combination Study</CardTitle>
          <CardDescription>
            Four approaches from unfitted baseline to gradient-boosted trees, evaluated strictly
            out-of-sample (expanding window, 3-month embargo purging the 3M forward-return
            overlap). Target: cross-sectional rank of forward 3M sector-relative alpha.
            Momentum controls included so the study answers "is this just momentum?".
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <RunBar
            result={result}
            job={job}
            running={running}
            onRun={run}
            testId="supersignal-runbar"
          />
          {res && (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <StatCard label="OOS Window">
                  <span className="text-sm">
                    {res.protocol?.oos_start} → {res.protocol?.oos_end}
                  </span>
                </StatCard>
                <StatCard label="OOS Rebalance Dates">{res.protocol?.oos_dates}</StatCard>
                <StatCard label="OOS Observations">
                  {res.protocol?.oos_observations?.toLocaleString()}
                </StatCard>
                <StatCard label="Best Approach (OOS IC)">
                  <span className="text-sm">{approaches?.[bestKey]?.label}</span>
                </StatCard>
              </div>
              {res.universe_note && (
                <div className="text-xs text-muted-foreground border-l-2 border-yellow-600 pl-3 py-1"
                  data-testid="supersignal-universe-note">
                  {res.universe_note}
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {res && (
        <>
          {/* Model comparison */}
          <Card data-testid="supersignal-comparison">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <CardTitle className="text-base">Out-of-Sample Model Comparison</CardTitle>
                <div className="flex gap-2 flex-wrap">
                  <ToggleGroup
                    testId="supersignal-universe"
                    options={[
                      { value: "investable", label: "Investable (ex-micro)" },
                      { value: "all", label: "All caps" },
                    ]}
                    value={universe}
                    onChange={setUniverse}
                  />
                  <ToggleGroup
                    testId="supersignal-bench"
                    options={[
                      { value: "sector", label: "vs Sector" },
                      { value: "spy", label: "vs S&P 500" },
                    ]}
                    value={bench}
                    onChange={setBench}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-muted-foreground text-xs">
                      <th className="text-left py-2 px-2 font-medium">Approach</th>
                      <th className="text-right py-2 px-2 font-medium">Mean IC</th>
                      <th className="text-right py-2 px-2 font-medium">IC t</th>
                      <th className="text-right py-2 px-2 font-medium">IC IR</th>
                      <th className="text-right py-2 px-2 font-medium">Top−Bottom</th>
                      <th className="text-right py-2 px-2 font-medium">NW t</th>
                      <th className="text-right py-2 px-2 font-medium">Dates +</th>
                      <th className="text-right py-2 px-2 font-medium">Sig</th>
                    </tr>
                  </thead>
                  <tbody>
                    {APPROACH_ORDER.filter((a) => approaches?.[a]).map((a) => {
                      const ap = approaches[a];
                      const e = ap[bench];
                      const isBest = a === bestKey;
                      return (
                        <tr
                          key={a}
                          onClick={() => setSelected(a)}
                          data-testid={`supersignal-row-${a}`}
                          className={`border-b cursor-pointer hover:bg-muted/40 ${
                            a === active ? "bg-muted/50" : ""
                          }`}
                        >
                          <td className="py-2 px-2">
                            <span className="font-medium">{ap.label}</span>
                            {isBest && (
                              <Badge className="ml-2 text-[10px] bg-indigo-600 hover:bg-indigo-600">
                                BEST
                              </Badge>
                            )}
                          </td>
                          <td className="text-right py-2 px-2 font-mono">
                            {e?.ic?.mean_ic ?? "-"}
                          </td>
                          <td className="text-right py-2 px-2 font-mono">{e?.ic?.nw_tstat ?? "-"}</td>
                          <td className="text-right py-2 px-2 font-mono">{e?.ic?.ic_ir ?? "-"}</td>
                          <td className="text-right py-2 px-2">
                            <AlphaVal value={e?.spread?.mean_pct} />
                          </td>
                          <td className="text-right py-2 px-2 font-mono">
                            {e?.spread?.nw_tstat ?? "-"}
                          </td>
                          <td className="text-right py-2 px-2 font-mono">
                            {e?.spread?.pct_positive ?? "-"}%
                          </td>
                          <td className="text-right py-2 px-2">
                            <SigBadge t={e?.spread?.nw_tstat} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <div className="text-xs text-muted-foreground mt-2">
                Click a row to inspect. Top−Bottom = D10−D1 (deciles) per 3M, Fama-MacBeth mean
                across OOS dates, Newey-West t-stats.
              </div>
            </CardContent>
          </Card>

          {/* Selected approach detail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <Card data-testid="supersignal-deciles">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">
                  {approach?.label} — OOS {ev?.n_quantiles === 10 ? "Decile" : "Quintile"} Alphas
                </CardTitle>
                <CardDescription className="text-xs">{approach?.desc}</CardDescription>
              </CardHeader>
              <CardContent>
                {ev ? (
                  <>
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={decileData} margin={{ top: 5, right: 5, left: -20 }}>
                        <CartesianGrid strokeDasharray="3 3" opacity={0.2} />
                        <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => `${v}%`} />
                        <Tooltip
                          content={({ active: act, payload, label }) =>
                            act && payload?.length ? (
                              <div className="bg-popover border rounded-md px-2 py-1 text-xs font-mono">
                                {label}: {payload[0].value > 0 ? "+" : ""}
                                {Number(payload[0].value).toFixed(2)}%
                              </div>
                            ) : null
                          }
                        />
                        <ReferenceLine y={0} stroke="currentColor" opacity={0.3} />
                        <Bar dataKey="alpha" radius={[3, 3, 0, 0]}>
                          {decileData.map((d, i) => (
                            <Cell
                              key={i}
                              fill={d.alpha >= 0 ? "#10b981" : "#ef4444"}
                              fillOpacity={0.85}
                            />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                    <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground">
                      <span>
                        Spread: <AlphaVal value={ev.spread?.mean_pct} /> · 95% CI [
                        {ev.spread?.ci_lo_pct}, {ev.spread?.ci_hi_pct}]% · n=
                        {ev.spread?.n_dates} dates
                      </span>
                      <SigBadge t={ev.spread?.nw_tstat} />
                    </div>
                  </>
                ) : (
                  <div className="text-sm text-muted-foreground py-8 text-center">
                    Insufficient data for this benchmark.
                  </div>
                )}
              </CardContent>
            </Card>

            <Card data-testid="supersignal-interpret">
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Interpretability — No Black Box</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {active === "gbt" && (
                  <ImportanceChart
                    data={approaches?.gbt?.importance}
                    title="Permutation importance (final model, pooled OOS sample)"
                    testId="gbt-importance"
                  />
                )}
                {active === "ridge" && (
                  <ImportanceChart
                    data={approaches?.ridge?.coefs}
                    title="Ridge coefficients (avg across folds, standardized features)"
                    testId="ridge-coefs"
                  />
                )}
                {active === "ic_weighted" && (
                  <ImportanceChart
                    data={approaches?.ic_weighted?.weights}
                    title="Average IC weights (walk-forward)"
                    testId="ic-weights"
                  />
                )}
                {active === "ew_rank" && (
                  <div className="text-sm text-muted-foreground">
                    Fixed equal weights on sector centrality, dynamic upside and nowcast 3M
                    gradient. Nothing is fitted — this is the benchmark the fitted approaches
                    must beat out-of-sample.
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Marginal contribution */}
          <Card data-testid="supersignal-marginal">
            <CardHeader className="pb-2">
              <CardTitle className="text-base">
                Marginal Contribution — Base Features Standalone (investable universe, same OOS dates)
              </CardTitle>
              <CardDescription className="text-xs">
                Every feature's own OOS IC vs forward 3M sector alpha, with its economic
                rationale. A composite only earns its keep if it beats the best single feature.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b text-muted-foreground">
                      <th className="text-left py-2 px-2 font-medium">Feature</th>
                      <th className="text-right py-2 px-2 font-medium">OOS IC</th>
                      <th className="text-right py-2 px-2 font-medium">NW t</th>
                      <th className="text-left py-2 px-2 font-medium">Economic rationale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(res.base_features_oos_ic || {})
                      .sort((a, b) => Math.abs(b[1].mean_ic) - Math.abs(a[1].mean_ic))
                      .map(([f, s]) => (
                        <tr key={f} className="border-b last:border-0 hover:bg-muted/30">
                          <td className="py-2 px-2 font-medium">{featLabel(f)}</td>
                          <td className="text-right py-2 px-2">
                            <AlphaVal value={s.mean_ic} digits={4} suffix="" />
                          </td>
                          <td className="text-right py-2 px-2 font-mono">{s.nw_tstat ?? "-"}</td>
                          <td className="py-2 px-2 text-muted-foreground">{s.rationale}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Cap bucket for best approach */}
          {(() => {
            const capData = universe === "investable"
              ? res.best_by_cap_bucket_investable
              : res.best_by_cap_bucket;
            if (!capData || Object.keys(capData).length === 0) return null;
            return (
              <Card data-testid="supersignal-capsplit">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base">
                    Best Approach ({approaches?.[bestKey]?.label}) by Cap Segment —{" "}
                    {universe === "investable" ? "investable model" : "all-cap model (artifact check)"}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    {Object.entries(capData).map(([b, e]) => (
                      <div key={b} className="border rounded-lg p-3 space-y-1">
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium">{CAP_LABELS[b] || b}</span>
                          <SigBadge t={e.spread?.nw_tstat} />
                        </div>
                        <div className="text-xl font-bold">
                          <AlphaVal value={e.spread?.mean_pct} />
                        </div>
                        <div className="text-xs text-muted-foreground">
                          Q5−Q1 per 3M · NW t={e.spread?.nw_tstat ?? "-"} · IC{" "}
                          {e.ic?.mean_ic ?? "-"} (t={e.ic?.nw_tstat ?? "-"})
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            );
          })()}
        </>
      )}
    </div>
  );
};
